import { AfterViewInit, Component, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormGroupDirective,
  Validators,
} from '@angular/forms';
import {
  AlternativeTransportEnum,
  Co2FacilityForm,
  Co2MaintenanceWindow,
  Co2YearFormGroup,
  CustomerDeclaration,
  EmissionType,
  RegisterCo2FacilityForm,
  StartMonthOfTransportationRequirements,
} from '../../../../interfaces/register_location';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FacilityCo2Type } from '@enums';
import {
  anlagenbeschreibungSpecialChars,
  maxBioRatio,
  maxCharacterLength,
  maxFirstYear,
  maxHours,
  maxLengthDescription,
  minAreaCharacterLength,
  minCharacterLength,
  minFirstYear,
  specialChars,
} from '../../../../../../shared/constants/form-constants';
import { ValidateExtrenumValueMatcher } from '../../../../../../shared/validators/validate-value-to-number';
import { Select } from '@ngxs/store';
import { AppStateSelectors } from '@appState/app-state.selector';
import { Observable, pairwise, startWith } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-register-co2-data-form',
  templateUrl: './register-co2-data-form.component.html',
  styleUrls: ['./register-co2-data-form.component.scss'],
})
export class RegisterCo2DataFormComponent implements OnInit, AfterViewInit {
  @Input()
  public step = 0;
  @Input()
  public years: number[] = [];
  @Output()
  public readonly facilityTypeEnum = FacilityCo2Type;
  public readonly facilityCo2Type = FacilityCo2Type;
  public readonly alternativeTransportEnum = AlternativeTransportEnum;
  public form: FormGroup<RegisterCo2FacilityForm>;
  public readonly emissionTypeArray = Object.keys(EmissionType)
    .filter(key => !isNaN(Number(EmissionType[key])))
    .map(key => ({ label: key, value: EmissionType[key] }));
  public readonly existingConsumerArray = Object.keys(CustomerDeclaration)
    .filter(key => !isNaN(Number(CustomerDeclaration[key])))
    .map(key => ({ label: key, value: CustomerDeclaration[key] }));
  public readonly alternativeTransportArray = Object.keys(
    AlternativeTransportEnum
  )
    .filter(key => !isNaN(Number(AlternativeTransportEnum[key])))
    .map(key => ({ label: key, value: AlternativeTransportEnum[key] }));
  public readonly startMonthTransportationRequirementsArray = Object.keys(
    StartMonthOfTransportationRequirements
  )
    .filter(key => !isNaN(Number(StartMonthOfTransportationRequirements[key])))
    .map(key => ({
      label: key,
      value: StartMonthOfTransportationRequirements[key],
    }));

  public dataSource;

  public validateExtrenumValueMatcher = new ValidateExtrenumValueMatcher();

  public displayedColumns = [
    'year',
    'ton',
    'hour',
    'bio_ratio',
    'peak_in_t_per_h',
  ];
  public showAlternativeTransportCustomInput = false;
  public showCustomerDeclartionInput = false;
  public hasNoneSelected = false;
  public maxLengthDescription = maxLengthDescription;
  public minCharacterLength = minCharacterLength;
  public minAreaCharacterLength = minAreaCharacterLength;
  public maxCharacterLength = maxCharacterLength;
  public maxBioRatio = maxBioRatio;
  public maxHours = maxHours;
  public maxFirstYear = maxFirstYear;
  public minFirstYear = minFirstYear;
  public specialCharsPattern = specialChars;
  public anlagenbeschreibungSpecialCharsPattern = anlagenbeschreibungSpecialChars;
  public maintenanceWindowCount = 1;

  @Select(AppStateSelectors.isUserOGE())
  public isUserOGE$: Observable<boolean>;

  constructor(private readonly parentFormGroup: FormGroupDirective) {}

  public get facilityForm(): FormGroup<Co2FacilityForm> {
    return this.form?.controls.facility as FormGroup;
  }

  public get typeId(): FacilityCo2Type {
    return this.facilityForm.controls.typeId.value;
  }

  public get alternativeTransportTypeIdControl(): FormControl {
    return this.form?.controls?.alternativeTransportTypeId;
  }

  public get maintenanceWindow2Control(): FormGroup<Co2MaintenanceWindow> {
    return this.form?.controls?.maintenanceWindow2;
  }

  public get maintenanceWindow3Control(): FormGroup<Co2MaintenanceWindow> {
    return this.form?.controls?.maintenanceWindow3;
  }

  public get alternativeTransportControl(): FormControl {
    return this.form?.controls?.alternativeTransport;
  }

  public get descriptionControl(): FormControl {
    return this.form?.controls.description;
  }

  public get customerDeclarationTypeIdControl(): FormControl {
    return this.form?.controls.customerDeclarationTypeId;
  }

  public getTitle(): string {
    if (this.typeId == this.facilityCo2Type.Einspeisung) {
      return 'registration.co2.data_type_consumer';
    } else if (this.typeId == this.facilityCo2Type.Ausspeisung) {
      return 'registration.co2.data_type_producer';
    } else if (this.typeId == this.facilityCo2Type.Hybrid) {
      return 'registration.co2.data_type_hybrid';
    }

    return '';
  }

  public ngOnInit() {
    this.form = this.parentFormGroup.control;
    this.dataSource = this.years.map(year => ({
      year: year,
      ton: 0,
      hour: 0,
      bio_ratio: 0,
      peak_in_t_per_h: 0,
    }));
    this.maintenanceWindow2Control.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(data => {
        if (data.start !== '' && this.maintenanceWindowCount < 2) {
          this.addMaintenanceWindow();
        }
      });
    this.maintenanceWindow3Control.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(data => {
        if (data.start !== '' && this.maintenanceWindowCount < 3) {
          this.addMaintenanceWindow();
        }
      });
    this.alternativeTransportTypeIdControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(typeId => {
        this.showAlternativeTransportCustomInput = typeId.includes(
          AlternativeTransportEnum.FREITEXT
        );

        if (typeId.includes(AlternativeTransportEnum.FREITEXT)) {
          this.alternativeTransportControl.setValidators([Validators.required]);
          this.form.updateValueAndValidity();
        } else {
          this.alternativeTransportControl.clearValidators();
          this.alternativeTransportControl.setValue(null);
          this.form.updateValueAndValidity();
        }

        if (typeId?.includes(AlternativeTransportEnum.NONE)) {
          this.alternativeTransportTypeIdControl.setValue(
            [AlternativeTransportEnum.NONE],
            { emitEvent: false }
          );
          this.hasNoneSelected = true;
          this.showAlternativeTransportCustomInput = false;
        } else {
          this.hasNoneSelected = false;
        }
      });

    this.customerDeclarationTypeIdControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(customerDeclarationTypeId => {
        this.showCustomerDeclartionInput =
          customerDeclarationTypeId == CustomerDeclaration.YES;
        if (this.showCustomerDeclartionInput) {
          // add required!
          this.form.controls.customerDeclaration.setValidators(
            Validators.required
          );
        } else {
          this.form.controls.customerDeclaration.removeValidators(
            Validators.required
          );
          this.form.controls.customerDeclaration.reset();
        }
      });

    this.form.controls.facility.controls.typeId.valueChanges
      .pipe(
        untilDestroyed(this),
        startWith(this.form.controls.facility.controls.typeId.getRawValue()),
        pairwise()
      )
      .subscribe(([prevId, typeId]) => {
        if (prevId !== typeId) {
          if (typeId === this.facilityCo2Type.Hybrid) {
            // einfach lassen
          } else if (prevId == this.facilityTypeEnum.Ausspeisung) {
            // del ausspeisung = consume
            this.resetList(this.form.controls.consume_list);
          } else if (prevId == this.facilityTypeEnum.Einspeisung) {
            // del einspeisung = produce_list
            this.resetList(this.form.controls.produce_list);
          } else if (
            prevId == this.facilityTypeEnum.Hybrid &&
            typeId == this.facilityTypeEnum.Einspeisung
          ) {
            this.resetList(this.form.controls.consume_list);
          } else if (
            prevId == this.facilityTypeEnum.Hybrid &&
            typeId == this.facilityTypeEnum.Ausspeisung
          ) {
            this.resetList(this.form.controls.produce_list);
          }
        }
      });
  }

  private resetList(formList: FormArray<FormGroup<Co2YearFormGroup>>) {
    formList.controls.forEach(control => {
      control.controls.ton.reset();
      control.controls.bio_ratio.reset();
      control.controls.hour.reset();
      control.controls.peak_in_t_per_h.reset();
    });
  }

  public ngAfterViewInit(): void {
    /* UGLY JS Resize textarea - with a hidden div */
    const textarea = document.querySelector<HTMLTextAreaElement>('.textarea-facility-description');
    if (!textarea) return; // Safety first
    const hiddenDiv = document.createElement('div');
    let content = null;
    hiddenDiv.classList.add('hidden-resize-div-for-textarea', 'textarea-facility-description');
    textarea.addEventListener('keyup', () => {
      textarea.parentNode.appendChild(hiddenDiv);
      content = (textarea).value.replaceAll('\n', '</br>');
      hiddenDiv.innerHTML = content;
      hiddenDiv.style.visibility = 'hidden';
      hiddenDiv.style.display = 'block';
      (textarea).style.height = hiddenDiv.offsetHeight + 'px';

      // make div invisible again
      hiddenDiv.style.visibility = 'visible';
      hiddenDiv.style.display = 'none';
    });
  }

  public checkForErrorsIn(
    formControl: AbstractControl,
    patternErrorKey = 'form.special_chars.not_allowed'
  ): string {
    if (formControl.hasError('required')) {
      return 'registration.co2.required';
    }

    if (
      formControl.hasError('min') ||
      formControl.hasError('max') ||
      formControl.hasError('minLength') ||
      formControl.hasError('maxLength')
    ) {
      return 'registration.co2.value_not_correct';
    }

    if (formControl.hasError('pattern')) {
      return patternErrorKey;
    }

    return '';
  }

  public getEmissionLabel(translate: string) {
    return 'registration.co2.emission_' + translate.toLowerCase();
  }

  public getCustomerDeclarationLabel(translate: string) {
    return 'registration.co2.customer_declaration_' + translate.toLowerCase();
  }

  public getAlternativeTypeLabel(translate: string) {
    return 'registration.co2.alternative_transport_' + translate.toLowerCase();
  }
  public getMonthLabel(translate: string) {
    return (
      'registration.co2.start_month_of_transportation_requirements_' +
      translate.toLowerCase()
    );
  }

  public addMaintenanceWindow() {
    this.maintenanceWindowCount++;
  }

  public deleteMaintenanceWindow() {
    if (this.maintenanceWindowCount === 3) {
      this.maintenanceWindow3Control.controls.start.setValue('');
      this.maintenanceWindow3Control.controls.end.setValue('');
    }
    if (this.maintenanceWindowCount === 2) {
      this.maintenanceWindow2Control.controls.start.setValue('');
      this.maintenanceWindow2Control.controls.end.setValue('');
    }
    this.maintenanceWindowCount--;
  }

  clearIfZero(control: FormControl<number>) {
    if (control.value === 0) {
      control.setValue(null);
    }
  }

  restoreIfEmpty(control: FormControl<number>) {
    if (control.value === null) {
      control.setValue(0);
    }
  }
}
